import React, { useState, useEffect } from "react";
import './App.css';
import {
  ResponsiveContainer,
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Line,
} from "recharts";

// Chest Girth Sufficiency App - 傾き＆平均値縦表示版
export default function App() {
  const [id, setId] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [birthDate, setBirthDate] = useState("");
  const [ageDays, setAgeDays] = useState(0);
  const [sex, setSex] = useState("オス/去勢");
  const [measured, setMeasured] = useState(0);
  const [records, setRecords] = useState([]);
  const [editIndex, setEditIndex] = useState(-1);
  const [slope, setSlope] = useState(0);
  const [avgRate, setAvgRate] = useState(0);

  // localStorage
  useEffect(() => {
    const saved = localStorage.getItem("chest_records_v3");
    if (saved) setRecords(JSON.parse(saved));
  }, []);
  useEffect(() => {
    localStorage.setItem("chest_records_v3", JSON.stringify(records));
  }, [records]);

  // 日齢自動計算
  useEffect(() => {
    if (birthDate) {
      const birth = new Date(birthDate);
      const calc = date ? new Date(date) : new Date();
      const diffTime = calc - birth;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      setAgeDays(diffDays > 0 ? diffDays : 0);
    }
  }, [birthDate, date]);

  // 標準胸囲
  function calcStandard(age, sex) {
    if (!age || age <= 0) return 0;
    if (sex === "オス/去勢")
      return -0.0004 * age * age + 0.4221 * age + 77.555;
    return -0.0005 * age * age + 0.4101 * age + 78.115;
  }

  // 推定体重
  function calcEstWeight(measured) {
    if (!measured || measured <= 0) return 0;
    return 55.256 - 2.11419 * measured + 0.024323 * measured * measured;
  }

  const standard = calcStandard(Number(ageDays), sex);
  const suffRate =
    standard > 0 ? (Number(measured) / Number(standard)) * 100 : 0;
  const estWeight = calcEstWeight(Number(measured));

  // 記録保存
  function onSave() {
    const rec = {
      id: id || "-",
      date,
      sex,
      birthDate,
      ageDays: Number(ageDays),
      measured: Number(measured),
      standard: Number(standard.toFixed(1)),
      suffRate: Number(suffRate.toFixed(1)),
      estWeight: Number(estWeight.toFixed(1)),
    };
    if (editIndex >= 0) {
      const copy = [...records];
      copy[editIndex] = rec;
      setRecords(copy);
      setEditIndex(-1);
    } else {
      setRecords([rec, ...records]);
    }
    setId("");
    setMeasured(0);
    setBirthDate("");
    setAgeDays(0);
  }

  function onEdit(i) {
    const r = records[i];
    setId(r.id);
    setDate(r.date);
    setSex(r.sex);
    setBirthDate(r.birthDate || "");
    setAgeDays(r.ageDays);
    setMeasured(r.measured);
    setEditIndex(i);
  }

  function onDelete(i) {
    if (!window.confirm("この記録を削除しますか？")) return;
    const copy = [...records];
    copy.splice(i, 1);
    setRecords(copy);
  }

  function exportCSV() {
    const header = [
      "ID",
      "Date",
      "BirthDate",
      "Sex",
      "AgeDays",
      "Measured(cm)",
      "Standard(cm)",
      "SuffRate(%)",
      "EstWeight(kg)",
    ];
    const rows = records.map((r) => [
      r.id,
      r.date,
      r.birthDate,
      r.sex,
      r.ageDays,
      r.measured,
      r.standard,
      r.suffRate,
      r.estWeight,
    ]);
    const csvContent = [header, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `chest_records_${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  // グラフ用データ整形
  const graphData = records
    .map((r) => ({ x: r.ageDays, y: r.suffRate }))
    .sort((a, b) => a.x - b.x);

  // 近似直線（最小二乗法）
  function linearRegression(data) {
    const n = data.length;
    if (n < 2) return { line: [], slope: 0, avg: 0 };
    const sumX = data.reduce((a, b) => a + b.x, 0);
    const sumY = data.reduce((a, b) => a + b.y, 0);
    const sumXY = data.reduce((a, b) => a + b.x * b.y, 0);
    const sumX2 = data.reduce((a, b) => a + b.x * b.x, 0);
    const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
    const intercept = (sumY - slope * sumX) / n;
    const avg = sumY / n;
    const minX = Math.min(...data.map((d) => d.x));
    const maxX = Math.max(...data.map((d) => d.x));
    return {
      line: [
        { x: minX, y: slope * minX + intercept },
        { x: maxX, y: slope * maxX + intercept },
      ],
      slope,
      avg,
    };
  }

  const { line: trendLine, slope: calcSlope, avg: calcAvg } =
    linearRegression(graphData);

  useEffect(() => {
    setSlope(calcSlope.toFixed(2));
    setAvgRate(calcAvg.toFixed(1));
  }, [records]);

  return (
    <div className="app-container">
      <h1>胸囲充足率アプリ</h1>

      <div className="card">
        <h2>入力フォーム</h2>
        <div className="input-group">
          <label>個体ID</label>
          <input type="text" value={id} onChange={(e) => setId(e.target.value)} />
        </div>

        <div className="input-group">
          <label>測定日</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>

        <div className="input-group">
          <label>生年月日</label>
          <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
        </div>

        <div className="input-group">
          <label>性別</label>
          <div style={{ display: "flex", gap: "1rem" }}>
            <label>
              <input type="radio" value="オス/去勢" checked={sex === "オス/去勢"} onChange={(e) => setSex(e.target.value)} /> オス/去勢
            </label>
            <label>
              <input type="radio" value="メス" checked={sex === "メス"} onChange={(e) => setSex(e.target.value)} /> メス
            </label>
          </div>
        </div>

        <div className="input-group">
          <label>日齢 (days)</label>
          <input type="number" value={ageDays} readOnly />
        </div>

        <div className="input-group">
          <label>測定胸囲 (cm)</label>
          <input type="number" value={measured} onChange={(e) => setMeasured(e.target.value)} />
        </div>

        <div className="button-group">
          <button onClick={onSave}>{editIndex >= 0 ? "更新" : "保存"}</button>
          <button onClick={() => { setId(""); setMeasured(0); setBirthDate(""); setAgeDays(0); setEditIndex(-1); }}>クリア</button>
        </div>
      </div>

      <div className="card result-card">
        <h2>結果</h2>
        <p>胸囲充足率: <span className={suffRate >= 100 ? "text-green" : suffRate < 80 ? "text-red" : "text-yellow"}>{suffRate.toFixed(1)}%</span></p>
        <p>標準胸囲: <span className="text-green">{standard ? standard.toFixed(1) : "—"} cm</span></p>
        <p>推定体重: <span className="text-green">{estWeight ? estWeight.toFixed(1) : "—"} kg</span></p>
      </div>

      {graphData.length > 1 && (
        <div className="card">
          <h2>胸囲充足率の牛群傾向</h2>
          <ResponsiveContainer width="100%" height={250}>
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" dataKey="x" name="日齢" unit="日" />
              <YAxis dataKey="y" name="胸囲充足率" unit="%" domain={[70, "auto"]} />
              <Tooltip />
              <Scatter data={graphData} fill="#4caf50" />
              <Line type="linear" data={trendLine} dataKey="y" stroke="#ff9800" dot={false} />
            </ScatterChart>
          </ResponsiveContainer>

          {/* 傾きと平均値（縦表示） */}
          <div style={{ marginTop: "1rem", textAlign: "center" }}>
            <div
              style={{
                display: "inline-block",
                backgroundColor: "#f0f4ff",
                border: "2px solid #4f46e5",
                borderRadius: "12px",
                padding: "10px 20px",
                boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
                lineHeight: "1.8",
              }}
            >
              <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#1e3a8a" }}>
                傾き：{slope > 0 ? "+" : ""}{slope}
              </div>
              <div style={{ fontSize: "1.2rem", fontWeight: "bold", color: "#1e3a8a" }}>
                平均胸囲充足率：{avgRate}%
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="card table-container">
        <div style={{display: "flex", justifyContent:"space-between", marginBottom:"0.5rem"}}>
          <h2>記録一覧</h2>
          <div style={{display:"flex", gap:"0.5rem"}}>
            <button onClick={exportCSV}>CSV出力</button>
            <button onClick={()=>{ localStorage.removeItem("chest_records_v3"); setRecords([]); }}>全削除</button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>測定日</th>
              <th>生年月日</th>
              <th>性別</th>
              <th>日齢</th>
              <th>測定(cm)</th>
              <th>標準(cm)</th>
              <th>充足率(%)</th>
              <th>推定体重(kg)</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {records.map((r, i) => (
              <tr key={i}>
                <td>{r.id}</td>
                <td>{r.date}</td>
                <td>{r.birthDate}</td>
                <td>{r.sex}</td>
                <td>{r.ageDays}</td>
                <td>{r.measured}</td>
                <td>{r.standard}</td>
                <td>{r.suffRate}</td>
                <td>{r.estWeight}</td>
                <td style={{display:"flex", gap:"0.3rem"}}>
                  <button onClick={()=>onEdit(i)}>編集</button>
                  <button onClick={()=>onDelete(i)}>削除</button>
                </td>
              </tr>
            ))}
            {records.length === 0 && (
              <tr><td colSpan="10" style={{padding:"0.5rem", color:"#555"}}>記録はありません</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: "2rem", textAlign: "center", color: "#4caf50", fontStyle: "italic", fontSize: "0.9rem" }}>
        © 2025 S.Hashimoto
      </div>
    </div>
  );
}
